import {Routes} from '@angular/router';
import {authGuard} from "@shared/services/security/auth.guard";
import {typeGuard} from "@shared/services/security/type-guard";
import {PageTypesEnum} from "@shared/services/router/page-types.enum";
// AppointmentsPage import intentionally kept unused — see the deactivated 'cdss/sessions'
// route below (UNIVAPLAT-2600, Epic UNIVAPLAT-2588).
// import {AppointmentsPage} from "@cdss/pages/appointments/appointments.page";
import {PatientRegistrationPage} from "@cdss/pages/patient-registration/patient-registration.page";
import {PageStylesEnum} from "@shared/services/router/page-styles.enum";
import {PatientOverviewV2Page} from "@cdss/pages/patient-overview-v2/patient-overview-v2.page";
import {DashboardV2Page} from "@cdss/pages/dashboard-v2/dashboard-v2.page";
import {PatientEditPage} from "@cdss/pages/patient-edit/patient-edit.page";
import {DraftPatientsPage} from "@cdss/pages/draft-patients/draft-patients.page";
import {PatientRiskSummaryPage} from "@cdss/pages/patient-risk-summary/patient-risk-summary.page";
import {AdminDashboardPage} from "@cdss/pages/admin-dashboard/admin-dashboard.page";
import {AdminMessagingPage} from "@cdss/pages/admin/messaging/admin-messaging.page";
import {ContractsListPage} from "@cdss/pages/contracts/list/contracts-list.page";
import {ContractNewPage} from "@cdss/pages/contracts/new/contract-new.page";
import {ContractDetailPage} from "@cdss/pages/contracts/detail/contract-detail.page";
import {Permissions} from "@models/permissions.enum";
import {roleGuard} from '@shared/services/security/role-guard';
import {PatientWeeklyDetailPage} from "@cdss/pages/patient-overview-v2/detail/weekly/patient/patient-weekly-detail.page";
import {CarerWeeklyDetailPage} from "@cdss/pages/patient-overview-v2/detail/weekly/carer/carer-weekly-detail.page";
import {PatientDailyDetailPage} from "@cdss/pages/patient-overview-v2/detail/daily/patient/patient-daily-detail.page";
import {CarerDailyDetailPage} from "@cdss/pages/patient-overview-v2/detail/daily/carer/carer-daily-detail.page";

import {OrganisationsListPage} from "@cdss/pages/organisations/list/organisations-list.page";
import {OrganisationNewPage} from "@cdss/pages/organisations/new/organisation-new.page";
import {OrganisationDetailPage} from "@cdss/pages/organisations/detail/organisation-detail.page";
import {ORGANISATIONS_LIST_ROUTE, ORGANISATION_NEW_ROUTE, ORGANISATION_DETAIL_ROUTE} from '@shared/services/router/organisation-routes';
import {CONTRACTS_LIST_ROUTE, CONTRACT_NEW_ROUTE, CONTRACT_DETAIL_ROUTE, contractDetailRoute} from '@shared/services/router/contract-routes';

// Re-exported for existing importers; new code imports from contract-routes directly.
export {CONTRACTS_LIST_ROUTE, CONTRACT_NEW_ROUTE, CONTRACT_DETAIL_ROUTE, contractDetailRoute};

export const cdssRoutes: Routes = [
    {
        path: 'cdss/admin-dashboard',
        component: AdminDashboardPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        // UNIVAPLAT-3142: unified warm page surface, matching dashboard-v2
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_ADMIN_DASHBOARD}
    },
    {
        path: CONTRACTS_LIST_ROUTE,
        component: ContractsListPage,
        title: 'Contracts',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CONTRACTS}
    },
    {
        // Declared before the future `:id` detail route so `new` is not read as an id.
        path: CONTRACT_NEW_ROUTE,
        component: ContractNewPage,
        title: 'New contract',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.CREATE_CONTRACT}
    },
    {
        // After `new` so `new` is not read as an id (UNIVAPLAT-3517).
        path: CONTRACT_DETAIL_ROUTE,
        component: ContractDetailPage,
        title: 'Contract',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CONTRACTS}
    },
    {
        path: ORGANISATIONS_LIST_ROUTE,
        component: OrganisationsListPage,
        title: 'Organisations',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_ORGANISATIONS}
    },
    {
        // Declared before the `:id` detail route so `new` is not read as an id.
        path: ORGANISATION_NEW_ROUTE,
        component: OrganisationNewPage,
        title: 'New organisation',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.CREATE_ORGANISATION}
    },
    {
        path: ORGANISATION_DETAIL_ROUTE,
        component: OrganisationDetailPage,
        title: 'Organisation',
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_ORGANISATIONS}
    },
    {
        path: 'cdss/admin/messaging/:userId',
        component: AdminMessagingPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.SEND_WHATSAPP_MESSAGE}
    },
    {
        path: 'cdss/dashboard',
        component: DashboardV2Page,
        canActivate: [authGuard, typeGuard, roleGuard],
        // CREAM = unified warm page surface for the univa-2026 dashboard
        // restyle (UNIVAPLAT-2643) — scoped to this route only.
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },

    {
        path: 'cdss/patient-overview/:id',
        component: PatientOverviewV2Page,
        canActivate: [authGuard, typeGuard, roleGuard],
        // UNIVAPLAT-2750: unified warm page surface, matching dashboard-v2
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-risk-summary/:id',
        component: PatientRiskSummaryPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-edit/:id',
        component: PatientEditPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/draft-patients',
        component: DraftPatientsPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-registration',
        component: PatientRegistrationPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-registration/:id',
        component: PatientRegistrationPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.GRAY, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },

    // UNIVAPLAT-2600 (Epic UNIVAPLAT-2588): 'cdss/sessions' ("my calendar") intentionally
    // deactivated — CometChat deprecation coupled this screen to endpoints being deactivated
    // in the backend (UNIVAPLAT-2589). AppointmentsPage and its modals are kept in the repo
    // for possible future reactivation; the route is unreachable so it falls through to
    // the '**' -> not-found route.
    // {
    //     path: 'cdss/sessions',
    //     component: AppointmentsPage,
    //     canActivate: [authGuard, typeGuard],
    //     data: {type: PageTypesEnum.CDSS, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    // },

    {
        path: 'cdss/patient-overview/:id/patient-weekly',
        component: PatientWeeklyDetailPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-overview/:id/carer-weekly',
        component: CarerWeeklyDetailPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-overview/:id/patient-daily',
        component: PatientDailyDetailPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
    {
        path: 'cdss/patient-overview/:id/carer-daily',
        component: CarerDailyDetailPage,
        canActivate: [authGuard, typeGuard, roleGuard],
        data: {type: PageTypesEnum.CDSS, style: PageStylesEnum.CREAM, permission: Permissions.VIEW_CLINICIAN_OPTIONS}
    },
];
